import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Text,
  Box,
  Flex,
  Button,
  FormControl,
  FormLabel,
  HStack,
  useMediaQuery,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuItemOption,
  MenuOptionGroup,
  Image,
} from '@chakra-ui/react';
import { useState, useEffect, useCallback } from 'react';
import { Form, Formik } from 'formik';
import { ChevronDownIcon } from '@chakra-ui/icons';
import Dropzone from 'react-dropzone';
import moment from 'moment';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { BsArrowRight } from 'react-icons/bs';

import FormField from 'components/FormField';
import TextArea from 'components/Textarea';
import UpdateBlockSchema from 'validation/schemas/UpdateBlockSchema';
import Pencil from 'components/Icons/Pencil';
import { handleFileDelete, handleFileUpload } from 'services/s3Service';
import FullScreenLoader from 'components/FullScreenLoader';
import { acceptedFormats } from 'utils/FileFormats';
import { CURRENCY_OPTIONS, MONTH_NAMES } from 'projectConstants';
import { UpdateModelTime } from 'redux/ModelsSlice';
import { getUserSubscriptionLimits } from 'redux/UserSlice';
import DatePickerInputButton from 'pages/ModelOverviewPage/DatePickerWithButton';

import blockImage from '../../../assets/blox_logo.png';

interface ModelSettingsModalProperties {
  isOpen: boolean;
  onClose: () => void;
  modelData?: {
    name?: string;
    description?: string;
    bannerImage?: string;
    icon?: string;
    startDate?: Date;
    endDate?: Date;
    currency?: string;
    createdAt?: string;
    createdDate?: string;
    ownerName?: string;
    ownerImage?: string;
    creator?: string;
    time_granularity?: 'M' | 'Y';
    plan_duration_periods?: number;
    starting_period?: string;
    ending_period?: string;
    fy_start_month?: string;
    time_properties?: {
      time_granularity?: 'M' | 'Y';
      plan_duration_periods?: number;
      starting_period?: string;
      ending_period?: string;
      fy_start_month?: string;
    };
  };
  onSave?: (form: any) => void;
}

const ModelSettingsModal = ({
  isOpen,
  onClose,
  modelData,
  onSave,
}: ModelSettingsModalProperties) => {
  const { modelId } = useParams();
  const dispatch: any = useDispatch();
  const [isMobile] = useMediaQuery('(max-width: 575px)');
  const [loader, setLoader] = useState(false);
  const [bannerLoader, setBannerLoader] = useState(false);
  const [bannerImage, setBannerImage] = useState(modelData?.bannerImage || null);
  const [selectedCurrency, setSelectedCurrency] = useState(
    CURRENCY_OPTIONS.find((c) => c.value === modelData?.currency) || CURRENCY_OPTIONS[5], // Default to INR
  );
  const timeProperties = modelData?.time_properties || modelData;
  const [timescale, setTimescale] = useState<'M' | 'Y'>(
    (timeProperties?.time_granularity?.toUpperCase() as 'M' | 'Y') || 'M',
  );
  const [periodAmount, setPeriodAmount] = useState(
    timeProperties?.plan_duration_periods?.toString() ||
      (timeProperties?.time_granularity?.toLowerCase() === 'm' ? '36' : '3'),
  );
  const [maxValue, setMaxValue] = useState(36);
  const [fyStartMonth, setFyStartMonth] = useState(timeProperties?.fy_start_month || 'January');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const UserSubscriptionLimitData = useSelector(getUserSubscriptionLimits);
  const timeGranualityLimit =
    UserSubscriptionLimitData?.subscription_features_limit?.model_timescale;

  // Helper function to parse date from period string
  const parseDateFromPeriod = (period: string, granularity: string): Date => {
    if (granularity === 'Y') {
      // Format: "YYYY"
      const year = Number.parseInt(period, 10);
      return new Date(year, 0, 1);
    }
    // Format: "DD MMM YY" or ISO string
    const parsed = new Date(period);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
    // Try parsing "DD MMM YY" format
    const parts = period.split(' ');
    if (parts.length === 3) {
      const day = Number.parseInt(parts[0], 10);
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = monthNames.indexOf(parts[1]);
      const year = 2000 + Number.parseInt(parts[2], 10);
      return new Date(year, month, day);
    }
    return new Date();
  };

  useEffect(() => {
    if (
      timeGranualityLimit === null &&
      UserSubscriptionLimitData?.current_subscription_name !== 'Ultimate'
    ) {
      setMaxValue(-1);
    } else {
      if (timescale.toLowerCase() === 'm') {
        setMaxValue(timeGranualityLimit);
      }
      if (timescale.toLowerCase() === 'y') {
        setMaxValue(timeGranualityLimit / 12);
      }
    }
  }, [timescale, UserSubscriptionLimitData, timeGranualityLimit]);

  // Parse dates from time_properties
  useEffect(() => {
    if (timeProperties) {
      const timeGranularity = timeProperties?.time_granularity?.toUpperCase() || 'M';
      setTimescale(timeGranularity as 'M' | 'Y');
      const value = Number.isNaN(timeProperties?.plan_duration_periods)
        ? timeGranularity === 'M'
          ? 36
          : 3
        : Number(timeProperties?.plan_duration_periods);
      setPeriodAmount(value.toString());
      setFyStartMonth(timeProperties?.fy_start_month || 'January');

      // Parse starting_period
      if (timeProperties?.starting_period) {
        const startDateParsed = parseDateFromPeriod(
          timeProperties.starting_period,
          timeGranularity,
        );
        setStartDate(startDateParsed);
      }

      // Parse ending_period or calculate from starting_period
      if (timeProperties?.ending_period) {
        const endDateParsed = parseDateFromPeriod(timeProperties.ending_period, timeGranularity);
        setEndDate(endDateParsed);
      } else if (timeProperties?.starting_period) {
        const startDateParsed = parseDateFromPeriod(
          timeProperties.starting_period,
          timeGranularity,
        );
        const endDateCalc = new Date(startDateParsed);
        const targetValue = timeGranularity === 'M' ? value : value * 12;
        const dateEnd = endDateCalc.setMonth(endDateCalc.getMonth() + targetValue);
        setEndDate(new Date(dateEnd));
      }
    }
  }, [timeProperties]);

  const formatDate = (date: string) => {
    return moment(date).format('DD MMMM YYYY');
  };

  const changeTimeInput = useCallback(
    (time: string, amount: string, startDateValue: Date | null, endDateValue?: Date | null) => {
      if (!modelId) return;

      const requestObject: any = {
        model_id: Number(modelId),
        request_body: {
          time_granularity: time,
          plan_duration_periods: Number(amount),
          fy_start_month: fyStartMonth,
        },
      };
      if (endDateValue) {
        const endInDate = endDateValue.toDateString();
        requestObject.request_body.ending_period =
          time === 'Y' ? dayjs(endInDate).format('YYYY') : dayjs(endInDate).format('DD MMM YY');
      }
      if (startDateValue !== null) {
        const startInDate = startDateValue.toDateString();
        requestObject.request_body.starting_period =
          time === 'Y' ? dayjs(startInDate).format('YYYY') : dayjs(startInDate).format('DD MMM YY');
      }
      dispatch(UpdateModelTime({ requestObject, toaster: true }));
    },
    [modelId, dispatch, fyStartMonth],
  );

  const onChangePeriod = (valueDiff: any) => {
    const newValue = valueDiff;
    if (Number(newValue) <= maxValue) {
      setPeriodAmount(newValue.toString());
    }
    if (maxValue === -1) {
      setPeriodAmount(newValue.toString());
    }
  };

  const startDateChange = (startDateValue: Date) => {
    if (!endDate) return '0';
    if (timescale.toLowerCase() === 'm') {
      const monthsApart =
        (endDate.getFullYear() - startDateValue.getFullYear()) * 12 +
        (endDate.getMonth() - startDateValue.getMonth());
      setPeriodAmount(`${monthsApart}`);
      onChangePeriod(monthsApart);
      return `${monthsApart}`;
    }
    const yearDifference = endDate.getFullYear() - startDateValue.getFullYear();
    setPeriodAmount(yearDifference.toString());
    onChangePeriod(`${yearDifference}`);
    return `${yearDifference}`;
  };

  const endDateChange = (endDateValue: Date) => {
    if (!startDate) return '0';
    if (timescale.toLowerCase() === 'm') {
      const monthsApart =
        (endDateValue.getFullYear() - startDate.getFullYear()) * 12 +
        (endDateValue.getMonth() - startDate.getMonth());
      setPeriodAmount(`${monthsApart}`);
      onChangePeriod(monthsApart);
      return `${monthsApart}`;
    }
    const yearDifference = endDateValue.getFullYear() - startDate.getFullYear();
    setPeriodAmount(yearDifference.toString());
    onChangePeriod(`${yearDifference}`);
    return `${yearDifference}`;
  };

  const onSelectStartDate = (date: Date) => {
    setStartDate(date);
    const diff = startDateChange(date);
    onChangePeriod(Number(diff));
    changeTimeInput(timescale, diff, date, endDate);
  };

  const onSelectEndDate = (date: Date) => {
    setEndDate(date);
    const diff = endDateChange(date);
    onChangePeriod(Number(diff));
    changeTimeInput(timescale, diff, startDate, date);
  };

  const onChangeTimescale = (value: 'M' | 'Y') => {
    setTimescale(value);
    setPeriodAmount(value === 'M' ? '36' : '3');
    const newStartDate = startDate || new Date();
    setStartDate(newStartDate);
    const endDateNew = new Date();
    const dateEnd = endDateNew.setMonth(endDateNew.getMonth() + (value === 'M' ? 36 : 36));
    const newEndDate = new Date(dateEnd);
    setEndDate(newEndDate);
    changeTimeInput(value, value === 'M' ? '36' : '3', newStartDate, newEndDate);
  };

  const onChangeFiscalYear = (month: string) => {
    setFyStartMonth(month);
    if (modelId && startDate && endDate) {
      const requestObject = {
        model_id: Number(modelId),
        request_body: {
          time_granularity: timescale,
          plan_duration_periods: Number(periodAmount),
          ending_period:
            timescale.toLowerCase() === 'y'
              ? dayjs(endDate).format('YYYY')
              : dayjs(endDate).format('DD MMM YY'),
          starting_period:
            timescale.toLowerCase() === 'y'
              ? dayjs(startDate).format('YYYY')
              : dayjs(startDate).format('DD MMM YY'),
          fy_start_month: `${month}`,
        },
      };
      dispatch(UpdateModelTime({ requestObject, toaster: true }));
    }
  };

  const handleSubmit = async (form: any) => {
    const submitData = {
      ...form,
      bannerImage,
      startDate,
      endDate,
      currency: selectedCurrency.value,
      time_granularity: timescale,
      plan_duration_periods: Number(periodAmount),
      fy_start_month: fyStartMonth,
    };
    if (onSave) {
      onSave(submitData);
    }
    onClose();
  };

  const handleBannerFileDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setBannerLoader(true);
      const file = acceptedFiles[0];
      const data = await handleFileUpload(file, 'blox-user-assets');
      setBannerImage(data);
      setBannerLoader(false);
    }
  };

  const handleBannerDelete = async () => {
    if (bannerImage) {
      setBannerLoader(true);
      await handleFileDelete(bannerImage, 'blox-user-assets').then((response: any) => {
        if (!response?.error) {
          setBannerImage(null);
        }
      });
      setBannerLoader(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={isMobile ? 'full' : 'xl'} isCentered>
      <ModalOverlay className='bg-black.900' opacity={0.5} />
      <ModalContent
        maxW='800px'
        h='60vh'
        maxH='60vh'
        borderRadius='lg'
        display='flex'
        flexDirection='column'
        overflow='hidden'
      >
        <Formik
          enableReinitialize
          initialValues={{
            name: modelData?.name || '',
            description: modelData?.description || '',
            icon: modelData?.icon || '',
          }}
          onSubmit={(form) => handleSubmit(form)}
          validationSchema={UpdateBlockSchema}
        >
          {({ errors, values, touched, setFieldValue }: any) => (
            <Form>
              <ModalHeader flexShrink={0} minW={0}>
                <Flex justifyContent='space-between' alignItems='center' minW={0}>
                  <Text fontSize='xl' fontWeight='bold' color='black.900' isTruncated>
                    Model Settings
                  </Text>
                  <ModalCloseButton />
                </Flex>
                {(modelData?.createdAt || modelData?.createdDate) && (
                  <Box mt={4} minW={0}>
                    <Flex mb={1} minW={0} flexWrap='wrap'>
                      <Text fontSize='16px' color='black.900'>
                        Created:{' '}
                      </Text>
                      <Text fontSize='16px' color='black.900' ml={2} isTruncated>
                        {formatDate(modelData?.createdAt || modelData?.createdDate || '')}
                      </Text>
                    </Flex>
                    {(modelData?.ownerName || modelData?.creator) && (
                      <Flex alignItems='center' mt={1} minW={0} flexWrap='wrap'>
                        <Text fontSize='16px' color='black.900' flexShrink={0}>
                          Owner:
                        </Text>
                        <Flex alignItems='center' minW={0} flex={1}>
                          {modelData?.ownerImage && (
                            <Image
                              height={13}
                              width={13}
                              ml={5}
                              borderRadius='50%'
                              src={modelData.ownerImage}
                              flexShrink={0}
                            />
                          )}
                          <Text fontSize='16px' color='black.900' ml={2} isTruncated minW={0}>
                            {modelData?.ownerName || modelData?.creator}
                          </Text>
                        </Flex>
                      </Flex>
                    )}
                  </Box>
                )}
              </ModalHeader>

              <ModalBody
                pb={6}
                pt={6}
                px={6}
                overflowY='auto'
                overflowX='hidden'
                flex={1}
                minH={0}
                maxH='calc(60vh - 200px)'
                minW={0}
                css={{
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#888',
                    borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#555',
                  },
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#888 #f1f1f1',
                }}
              >
                <Flex direction='column' gap={6} minW={0}>
                  {/* Banner Image Section */}
                  <FormControl>
                    <FormLabel fontSize='sm' fontWeight='semibold' mb={2} color='black.900'>
                      Banner image
                    </FormLabel>
                    <Text fontSize='xs' color='black.900' mb={2}>
                      Recommended size: 1080x1920px.
                    </Text>
                    <Box position='relative'>
                      {bannerLoader ? (
                        <FullScreenLoader
                          bgColor='linear-gradient(#FFFFFF, #F3F3F3)'
                          style={{ position: 'absolute', borderRadius: '8px' }}
                          height='200px'
                          width='100%'
                        />
                      ) : null}
                      <Dropzone
                        onDrop={handleBannerFileDrop}
                        accept={acceptedFormats}
                        maxSize={5 * 1024 * 1024} // 5MB
                      >
                        {({ getRootProps, getInputProps, isDragActive }) => (
                          <Box
                            {...getRootProps()}
                            border='2px dashed'
                            borderColor={isDragActive ? 'purple.500' : 'gray.300'}
                            borderRadius='md'
                            p={8}
                            textAlign='center'
                            cursor='pointer'
                            bg={isDragActive ? 'purple.50' : 'gray.50'}
                            _hover={{ bg: 'gray.100' }}
                            minH='200px'
                            display='flex'
                            flexDirection='column'
                            alignItems='center'
                            justifyContent='center'
                            position='relative'
                          >
                            <input {...getInputProps()} />
                            {bannerImage ? (
                              <Flex direction='column' alignItems='center' gap={2}>
                                <Image
                                  src={bannerImage}
                                  alt='Banner preview'
                                  maxH='150px'
                                  borderRadius='8px'
                                />
                                <Button
                                  size='sm'
                                  colorScheme='red'
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleBannerDelete();
                                  }}
                                >
                                  Remove
                                </Button>
                              </Flex>
                            ) : (
                              <>
                                <Box fontSize='4xl' color='gray.400' mb={2}>
                                  📷
                                </Box>
                                <Text fontSize='sm' color='black.900' mb={1}>
                                  Click to upload or drag and drop
                                </Text>
                                <Text fontSize='xs' color='black.900'>
                                  JPG, JPEG, PNG less than 5MB
                                </Text>
                              </>
                            )}
                          </Box>
                        )}
                      </Dropzone>
                    </Box>
                  </FormControl>

                  {/* Model Timeline Section */}
                  <FormControl>
                    <FormLabel fontSize='sm' fontWeight='semibold' mb={2} color='black.900'>
                      Model timeline
                    </FormLabel>
                    <Flex direction='column' gap={4}>
                      {/* Time Granularity */}
                      <Box>
                        <FormLabel fontSize='xs' color='black.900' mb={1}>
                          Time Granularity
                        </FormLabel>
                        <Menu closeOnSelect={true}>
                          <MenuButton
                            as={Button}
                            rightIcon={<ChevronDownIcon />}
                            width='100%'
                            textAlign='left'
                            bg='white'
                            border='1px solid'
                            borderColor='gray.300'
                            color='black.900'
                            _hover={{ bg: 'gray.50' }}
                          >
                            {timescale === 'M' ? 'Monthly' : 'Yearly'}
                          </MenuButton>
                          <MenuList>
                            <MenuOptionGroup value={timescale} type='radio'>
                              <MenuItemOption value='M' onClick={() => onChangeTimescale('M')}>
                                Monthly
                              </MenuItemOption>
                              <MenuItemOption value='Y' onClick={() => onChangeTimescale('Y')}>
                                Yearly
                              </MenuItemOption>
                            </MenuOptionGroup>
                          </MenuList>
                        </Menu>
                      </Box>

                      {/* Fiscal Year Start Month (only for Monthly) */}
                      {timescale === 'M' && (
                        <Box>
                          <FormLabel fontSize='xs' color='black.900' mb={1}>
                            Fiscal year start month
                          </FormLabel>
                          <Menu closeOnSelect={true}>
                            <MenuButton
                              as={Button}
                              rightIcon={<ChevronDownIcon />}
                              width='100%'
                              textAlign='left'
                              bg='white'
                              border='1px solid'
                              borderColor='gray.300'
                              color='black.900'
                              _hover={{ bg: 'gray.50' }}
                            >
                              {fyStartMonth}
                            </MenuButton>
                            <MenuList maxH='200px' overflowY='auto'>
                              <MenuOptionGroup value={fyStartMonth} type='radio'>
                                {MONTH_NAMES.map((month: string) => (
                                  <MenuItemOption
                                    key={month}
                                    value={month}
                                    onClick={() => onChangeFiscalYear(month)}
                                  >
                                    {month}
                                  </MenuItemOption>
                                ))}
                              </MenuOptionGroup>
                            </MenuList>
                          </Menu>
                        </Box>
                      )}

                      {/* Start and End Dates */}
                      <Box>
                        <FormLabel fontSize='xs' color='black.900' mb={2}>
                          Timescale
                        </FormLabel>
                        <Flex justify='start' align='center' pb={3}>
                          <DatePickerInputButton
                            clearSection={false}
                            clearDateInput={() => setStartDate(null)}
                            selected={startDate}
                            onChange={onSelectStartDate}
                            timescale={timescale}
                            isDisabled={false}
                          />
                          <Flex px={2}>
                            <BsArrowRight color='black' style={{ width: '20px', height: '20px' }} />
                          </Flex>
                          <DatePickerInputButton
                            clearSection={false}
                            clearDateInput={() => setEndDate(null)}
                            selected={endDate}
                            onChange={onSelectEndDate}
                            timescale={timescale}
                            isDisabled={false}
                            minDate={startDate || undefined}
                          />
                        </Flex>
                        <Text fontSize='xs' color='black.900' ps={1}>
                          {timescale === 'M'
                            ? `${Number(periodAmount) + 1} months`
                            : `${Number(periodAmount) + 1} years`}
                        </Text>
                      </Box>
                    </Flex>
                  </FormControl>

                  {/* Currency Section */}
                  <FormControl>
                    <FormLabel fontSize='sm' fontWeight='semibold' mb={2} color='black.900'>
                      Currency
                    </FormLabel>
                    <Menu>
                      <MenuButton
                        as={Button}
                        rightIcon={<ChevronDownIcon />}
                        width='100%'
                        textAlign='left'
                        bg='white'
                        border='1px solid'
                        borderColor='gray.300'
                        color='black.900'
                        _hover={{ bg: 'gray.50' }}
                        leftIcon={<Text>💰</Text>}
                        isTruncated
                      >
                        {selectedCurrency ? `${selectedCurrency.label}` : 'Select Currency'}
                      </MenuButton>
                      <MenuList maxH='300px' overflowY='auto'>
                        {CURRENCY_OPTIONS.map((currency) => (
                          <MenuItem
                            key={currency.value}
                            onClick={() => {
                              setSelectedCurrency(currency);
                            }}
                          >
                            {currency.label}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  </FormControl>

                  {/* Model Name Section */}
                  <FormField
                    label='Model name'
                    name='name'
                    type='text'
                    labelStyles={{
                      fontSize: '15px',
                      color: '#212121',
                    }}
                    inputStyles={{
                      maxWidth: '300px',
                      boxShadow: '0px 0px 0px 1.5px #EEF1F6',
                      height: '35px',
                      paddingInlineStart: '8px',
                    }}
                    touched={touched.name}
                    error={errors.name}
                  />

                  {/* Description Section */}
                  <TextArea label='Model description' name='description' />

                  {/* Thumbnail Section */}
                  <FormControl>
                    <FormLabel fontSize='sm' fontWeight='semibold' mb={2} color='black.900'>
                      Thumbnail
                    </FormLabel>
                    <Flex
                      minH={isMobile ? '120px' : 'auto'}
                      height='200px'
                      width='100%'
                      position='relative'
                      justifyContent='end'
                    >
                      {loader ? (
                        <FullScreenLoader
                          bgColor='linear-gradient(#FFFFFF, #F3F3F3)'
                          style={{ position: 'absolute' }}
                          height='100%'
                          width='100%'
                        />
                      ) : (
                        <Image
                          position='absolute'
                          borderRadius='12px'
                          height='100%'
                          width='100%'
                          objectFit='contain'
                          bg='linear-gradient(#FFFFFF, #F3F3F3)'
                          src={values?.icon || blockImage}
                        />
                      )}
                      <Menu variant='primary' placement='bottom-end'>
                        <MenuButton height='fit-content' zIndex={2} mr={4} mt={2} type='button'>
                          <Pencil
                            width={5}
                            height={6}
                            color='#bdbdbd'
                            style={{
                              cursor: 'pointer',
                            }}
                          />
                        </MenuButton>
                        <MenuList minWidth='160px !important' zIndex={2}>
                          <MenuItem>
                            <Dropzone
                              accept={acceptedFormats}
                              onDrop={async (file: any) => {
                                setLoader(true);
                                const data = await handleFileUpload(file[0], 'blox-user-assets');
                                setLoader(false);
                                setFieldValue('icon', data);
                              }}
                              multiple={false}
                            >
                              {({ getRootProps, getInputProps }) => (
                                <section style={{ width: '100%' }}>
                                  <div style={{ width: '100%' }} {...getRootProps()}>
                                    <input {...getInputProps()} />
                                    <p>Update</p>
                                  </div>
                                </section>
                              )}
                            </Dropzone>
                          </MenuItem>
                          {values.icon && (
                            <MenuItem
                              onClick={async () => {
                                setLoader(true);
                                await handleFileDelete(values?.icon, 'blox-user-assets').then(
                                  (response: any) => {
                                    if (!response?.error) {
                                      setFieldValue('icon', '');
                                    }
                                  },
                                );
                                setLoader(false);
                              }}
                            >
                              Remove image
                            </MenuItem>
                          )}
                        </MenuList>
                      </Menu>
                    </Flex>
                  </FormControl>
                </Flex>
              </ModalBody>

              <ModalFooter flexShrink={0} borderTop='1px solid' borderColor='gray.200' bg='white'>
                <HStack spacing={4} width='100%' justifyContent='flex-end'>
                  <Button
                    variant='outline'
                    onClick={onClose}
                    bg='purple.100'
                    color='purple.700'
                    borderColor='purple.300'
                    _hover={{ bg: 'purple.200' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    bg='purple.600'
                    color='white'
                    _hover={{ bg: 'purple.700' }}
                    type='submit'
                    disabled={loader}
                  >
                    Save Changes
                  </Button>
                </HStack>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
};

export default ModelSettingsModal;
